/* eslint-disable no-restricted-globals, no-undef */

// Get the crypto object — available as a global in browsers and Node.js 19+.
// In Node test environments, use a dynamic require to avoid bundler polyfills.
const getCrypto = () => {
  if (typeof window !== 'undefined' && window.crypto) return window.crypto;
  if (typeof globalThis !== 'undefined' && globalThis.crypto) return globalThis.crypto;
  if (typeof process !== 'undefined' && process.versions?.node) {
    try {
      // eslint-disable-next-line no-new-func
      const req = new Function('return require')();
      const nodeCrypto = req('crypto');
      return nodeCrypto.webcrypto || nodeCrypto;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Generate a cryptographically random invite token (UUID v4).
 *
 * Uses the Web Crypto API's randomUUID() when available,
 * falls back to a manual implementation using getRandomValues().
 *
 * @returns {string} UUID v4 string (e.g., "f47ac10b-58cc-4372-a567-0e02b2c3d479")
 */
export function generateInviteToken() {
  const c = getCrypto();

  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }

  // Fallback using getRandomValues or Node.js randomBytes
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else if (c && typeof c.randomBytes === 'function') {
    const buf = c.randomBytes(16);
    bytes.set(buf);
  } else {
    // Last resort: Math.random (not cryptographically secure, but functional)
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Set version (4) and variant (10xx) bits per RFC 4122
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * Validate that a string is a valid UUID v4 format.
 *
 * @param {string} token
 * @returns {boolean}
 */
export function isValidInviteToken(token) {
  if (!token || typeof token !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(token);
}
